"use client";

import Link from "next/link";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { track, EVENTS } from "@/lib/analytics";

export function LandingHeader() {
  // Use checkbox hack instead of useState for mobile menu
  // This keeps the component simple and CSR-compatible without React state
  const { t, locale } = useI18n();

  const navLinks = [
    { label: t("landing.nav.product"), href: "/product/treasury" },
    { label: t("landing.nav.pricing"), href: "/pricing" },
    { label: locale === "en" ? "Knowledge Base" : "База знаний", href: "/knowledge" },
    { label: t("landing.footer.blog") || "Blog", href: "/blog" },
    { label: t("landing.footer.about") || "About", href: "/about" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-md border-b border-slate-200/60">
      {/* Mobile Menu Toggle (Checkbox Hack) — sibling of menu via peer */}
      <input
        type="checkbox"
        id="mobile-menu-toggle"
        className="hidden peer"
        aria-label="Toggle mobile menu"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link
            href="/"
            className="font-bold text-xl sm:text-2xl text-keiro-green-dark hover:text-keiro-green transition-colors"
          >
            Keiront
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-keiro-gray-600 hover:text-keiro-green-dark font-medium transition-colors text-sm"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            <Button asChild variant="outline" size="sm">
              <Link href="/login">{t("landing.nav.login")}</Link>
            </Button>
            <Button asChild variant="default" size="sm">
              <Link
                href="/register"
                onClick={() =>
                  track(EVENTS.registerStart, { section: "header", placement: "desktop" })
                }
              >
                {t("landing.nav.trial")}
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Toggle Label */}
          <label
            htmlFor="mobile-menu-toggle"
            className="md:hidden flex items-center cursor-pointer text-keiro-green-dark hover:text-keiro-green transition-colors"
          >
            <Menu size={24} />
          </label>
        </div>
      </div>

      {/* Mobile Navigation Menu — sibling of input via peer */}
      <div className="hidden peer-checked:flex md:hidden flex-col gap-4 pb-4 pt-4 px-4 sm:px-6 border-t border-slate-200/60 bg-white">
        {navLinks.map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="text-keiro-gray-600 hover:text-keiro-green-dark font-medium transition-colors block px-2 py-2"
            onClick={() => {
              const checkbox = document.getElementById("mobile-menu-toggle") as HTMLInputElement;
              if (checkbox) checkbox.checked = false;
            }}
          >
            {link.label}
          </a>
        ))}

        <div className="flex flex-col gap-2 pt-2 border-t border-slate-200/60">
          <div className="flex justify-center">
            <LanguageSwitcher />
          </div>
          <Button asChild variant="outline" size="sm" className="w-full">
            <Link href="/login">{t("landing.nav.login")}</Link>
          </Button>
          <Button asChild variant="default" size="sm" className="w-full">
            <Link
              href="/register"
              onClick={() =>
                track(EVENTS.registerStart, { section: "header", placement: "mobile" })
              }
            >
              {t("landing.nav.trial")}
            </Link>
          </Button>
        </div>

        {/* Close icon indicator (optional) */}
        <label
          htmlFor="mobile-menu-toggle"
          className="absolute top-5 right-4 cursor-pointer text-keiro-green-dark"
          aria-label="Close menu"
        >
          <X size={24} />
        </label>
      </div>
    </header>
  );
}
